import { DIGEST_CADENCES } from '@repo/shared-types';
import { z } from 'zod';

/** `PATCH /api/bots/:botId/notifications/:userId` body (bot-notifications design,
 * docs/superpowers/specs/2026-08-21-bot-notifications-design.md §API) — a partial of
 * `NotificationPrefsDto`'s four configurable fields (`firstAnswer` is unchanged/not
 * configurable — see that DTO's own doc comment). `.strict()` rejects an unknown key rather
 * than silently dropping it, same posture as `botSettingsSchema`'s object fields; the
 * `.refine` below rejects an empty body — `{}` would otherwise validate successfully and
 * hit the repo as a no-op write that still bumps `updatedAt`. */
export const notificationPrefsPatchSchema = z
  .object({
    newLead: z.boolean().optional(),
    unansweredDigest: z.enum(DIGEST_CADENCES).optional(),
    crawlFailed: z.boolean().optional(),
    weeklySummary: z.boolean().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one preference field is required',
  });

export type NotificationPrefsPatchInput = z.infer<typeof notificationPrefsPatchSchema>;

/** `POST /api/notifications/unsubscribe` body (bot-notifications design §"Unsubscribe /
 * manage") — the opaque two-segment token every notification email's link carries
 * (`signUnsubscribeToken`, `apps/api/src/lib/unsubscribe-token.ts`). Bounds are generous —
 * the real token is well under 200 chars — just enough to reject an obviously-wrong body
 * (empty string, a multi-KB paste) before it ever reaches `verifyUnsubscribeToken`. */
export const unsubscribeSchema = z.object({
  token: z.string().min(20).max(1000),
});

export type UnsubscribeInput = z.infer<typeof unsubscribeSchema>;
